import React from "react";
import {
    faArrowDown,
    faArrowTurnDownLeft,
    faArrowUp,
} from "@fortawesome/pro-regular-svg-icons";
import { Span } from "@singlestore/fusion/components/typography";
import algoliasearch from "algoliasearch/lite";
import { usePageHistoryContext } from "contexts/PageHistoryContext";
import { useRecaptcha } from "contexts/RecaptchaContext";
import { useSearchModalContext } from "contexts/SearchModalContext";
import { navigate } from "gatsby";
import { usePrevious } from "hooks/use-previous";
import { useUrlParams } from "hooks/use-url-params";
import {
    InstantSearch,
    useHits,
    useSearchBox,
} from "react-instantsearch-hooks-web";

import { FaIcon } from "components/FaIcon/FaIcon";
import { KeyboardShortcut } from "components/KeyboardShortcut/KeyboardShortcut";
import { Modal } from "components/Modal/Modal";
import { SearchInput } from "components/SearchModal/SearchInput";
import { SearchResults } from "components/SearchModal/SearchResults";
import { SearchVersionSelector } from "components/SearchModal/SearchVersionSelector";

import "./SearchModal.scss";

const RECAPTCHA_THRESHOLD = 0.2;

if (
    !process.env.GATSBY_ALGOLIA_APPLICATION_ID ||
    !process.env.GATSBY_ALGOLIA_PUBLIC_API_KEY
) {
    throw new Error("Algolia credentials not set.");
}

const searchClient = algoliasearch(
    process.env.GATSBY_ALGOLIA_APPLICATION_ID as string,
    process.env.GATSBY_ALGOLIA_PUBLIC_API_KEY as string
);

const scrollIntoView = (index: number) => {
    const searchContainer = document.querySelector(".search-results-container");
    const hitList = searchContainer?.querySelector(".hit-list");

    const hit = hitList?.children?.item(index) as Maybe<HTMLElement>;

    if (searchContainer && hit) {
        const offsetBottom = hit.offsetTop + hit.clientHeight;
        const scrollBottom =
            searchContainer.scrollTop + searchContainer.clientHeight;

        const belowTopScroll = hit.offsetTop > searchContainer.scrollTop;
        const aboveBottomScroll = offsetBottom < scrollBottom;
        const isInView = belowTopScroll && aboveBottomScroll;

        if (!isInView) {
            hit.scrollIntoView({ behavior: "smooth" });
        }
    }
};

function BottomBar() {
    return (
        <div className="bottom-bar">
            <div className="shortcut-group">
                <div className="keys">
                    <KeyboardShortcut>
                        <FaIcon icon={faArrowTurnDownLeft} />
                    </KeyboardShortcut>
                </div>

                <Span variant="body-2">to select</Span>
            </div>

            <div className="shortcut-group">
                <div className="keys">
                    <KeyboardShortcut>
                        <FaIcon icon={faArrowUp} />
                    </KeyboardShortcut>
                    <KeyboardShortcut>
                        <FaIcon icon={faArrowDown} />
                    </KeyboardShortcut>
                </div>

                <Span variant="body-2">to navigate</Span>
            </div>

            <div className="shortcut-group">
                <div className="keys">
                    <KeyboardShortcut>esc</KeyboardShortcut>
                </div>

                <Span variant="body-2">to close</Span>
            </div>
        </div>
    );
}

function SearchResultsResolver() {
    const { query } = useSearchBox();
    const { hits } = useHits();
    const { state: historyState } = usePageHistoryContext();

    if (query) {
        return <SearchResults hits={hits} />;
    } else {
        return <SearchResults showingRecentlyViewed hits={historyState} />;
    }
}

function shouldBlockQuery(query?: Nullable<string>) {
    return !query;
}

export function SearchModal() {
    const { state, dispatch } = useSearchModalContext();
    const { getUrlParam, setUrlParams } = useUrlParams();
    const { recaptchaScore, generateRecaptchaScore } = useRecaptcha();

    const handleKeyDown = React.useCallback(
        (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "k") {
                event.stopPropagation();
                event.preventDefault();
                dispatch({ type: "toggle_search_modal_open" });
                return false;
            }

            // all other options are only used when the modal is open
            if (!state.searchModalOpen) {
                return;
            }

            if (event.key === "Escape") {
                event.stopPropagation();
                event.preventDefault();
                dispatch({ type: "set_search_modal_open", value: false });
                return false;
            }

            if (event.key === "ArrowUp") {
                event.stopPropagation();
                event.preventDefault();

                dispatch({ type: "decrement_result_focus_index" });
            }

            if (event.key === "ArrowDown") {
                event.stopPropagation();
                event.preventDefault();

                dispatch({ type: "increment_result_focus_index" });
            }

            if (event.key === "Enter") {
                event.stopPropagation();
                event.preventDefault();
                navigate(state.hits[state.resultFocusIndex].path);
            }
        },
        [state.searchModalOpen, state.hits, state.resultFocusIndex, dispatch]
    );

    // when the hits or index changes, scroll it into view
    React.useEffect(() => {
        scrollIntoView(state.resultFocusIndex);
    }, [state.resultFocusIndex]);

    React.useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown]);

    React.useEffect(() => {
        const searchQuery = getUrlParam("query");
        const productFilter = getUrlParam("product");

        if (searchQuery || productFilter) {
            dispatch({ type: "set_search_modal_open", value: true });
        }
    }, [getUrlParam, dispatch]);

    React.useEffect(() => {
        // whenever the search results change we focus on the first result
        dispatch({ type: "set_result_focus_index", value: 0 });
    }, [dispatch, state.hits]);

    const prevSearchModalOpen = usePrevious<boolean>(state.searchModalOpen);

    React.useEffect(() => {
        // if we are moving from the overlay being open to being not open
        if (!state.searchModalOpen && prevSearchModalOpen) {
            setUrlParams({
                query: undefined,
                product: undefined,
                version: undefined,
            });

            dispatch({ type: "set_is_404_search", value: false });
        }
    }, [state.searchModalOpen, prevSearchModalOpen, setUrlParams, dispatch]);

    React.useEffect(() => {
        const setSearchBlocked = async () => {
            let score = recaptchaScore;
            if (score === null) {
                // we haven't set a recaptcha score yet
                score = await generateRecaptchaScore("open_search");
            }

            if (score <= RECAPTCHA_THRESHOLD) {
                dispatch({ type: "set_search_status", value: "blocked" });
            } else {
                dispatch({ type: "set_search_status", value: "allowed" });
            }
        };

        if (state.searchModalOpen) {
            setSearchBlocked();
        }
    }, [
        state.searchModalOpen,
        recaptchaScore,
        generateRecaptchaScore,
        dispatch,
    ]);

    const setOpen = React.useCallback(
        (open: boolean) => {
            dispatch({ type: "set_search_modal_open", value: open });
        },
        [dispatch]
    );

    return (
        <Modal
            fullHeight
            open={state.searchModalOpen}
            setOpen={setOpen}
            className="search-modal"
            size="lg"
        >
            <InstantSearch
                indexName="singlestore_docs"
                // Avoid making a search request if there is no query
                // https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/react-hooks/#detecting-empty-search-requests
                searchClient={{
                    ...searchClient,
                    search(requests: any) {
                        if (
                            requests.every(({ params }: { params: any }) =>
                                shouldBlockQuery(params?.query)
                            )
                        ) {
                            return Promise.resolve({
                                results: requests.map(() => ({
                                    hits: [],
                                    nbHits: 0,
                                    nbPages: 0,
                                    page: 0,
                                    processingTimeMS: 0,
                                    hitsPerPage: 0,
                                    exhaustiveNbHits: false,
                                    query: "",
                                    params: "",
                                })),
                            });
                        }

                        return searchClient.search(requests);
                    },
                }}
            >
                <div className="input-container">
                    <SearchInput />
                    <SearchVersionSelector />
                </div>

                <SearchResultsResolver />
                <BottomBar />
            </InstantSearch>
        </Modal>
    );
}
